/**
 * Minimal Telegram WebApp SDK wrapper.
 *
 * The official SDK (loaded via <script> in index.html) attaches to
 * `window.Telegram.WebApp`. We wrap it so:
 *  - Code outside Telegram still works (SDK is undefined → no-ops)
 *  - We get TS types instead of `any`
 *  - The brand discipline (force-light, ignore TG color scheme) is
 *    centralised here per memory: feedback_force_light_theme.md
 *
 * Per the Sharp8 brand: we set our OWN colors regardless of the TG
 * dark/light scheme. The mini-app's identity is dark navy + cyan; we
 * don't take the user's TG theme into account.
 */

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      username?: string;
      photo_url?: string;
      language_code?: string;
    };
    auth_date?: number;
    hash?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;

  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;

  BackButton: {
    isVisible: boolean;
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };

  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    setText(text: string): void;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };

  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

// Cream / paper bg — matches the mini-app's light-mode brand
// (per memory: feedback_force_light_theme.md). Telegram dark color
// scheme is ignored — we paint our own theme regardless.
const APP_BG     = '#F6F4EE';
const APP_HEADER = '#F6F4EE';

/** True when running inside Telegram (vs a browser tab). */
export function inTelegram(): boolean {
  return Boolean(window.Telegram?.WebApp?.initData);
}

/** Returns the WebApp instance if present, else null. */
export function tg(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/**
 * Boot sequence — call once at app startup.
 * Tells Telegram we're ready, expands to full viewport, pins header
 * and background to the Sharp8 navy regardless of the user's TG theme.
 */
export function bootTelegram(): void {
  const w = tg();
  if (!w) return;

  try {
    w.ready();
    w.expand();
    w.setHeaderColor(APP_HEADER);
    w.setBackgroundColor(APP_BG);
  } catch (err) {
    console.warn('[sharp8] Telegram boot failed:', err);
  }
}

/** Get the current Telegram user (or null outside Telegram). */
export function tgUser() {
  return tg()?.initDataUnsafe.user ?? null;
}

/** Haptic tick — safe no-op if SDK or HapticFeedback unavailable. */
export function haptic(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light'): void {
  try {
    tg()?.HapticFeedback?.impactOccurred(style);
  } catch {
    // best-effort, ignore
  }
}
